import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function AcceptInvite() {
  const { token } = useParams();
  const { register, user } = useAuth();
  const navigate = useNavigate();
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState("");
  const [form, setForm] = useState({ name: "", password: "" });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get(`/public/invite/${token}`)
      .then(({ data }) => setInvite(data))
      .catch((e) => setError(formatApiError(e.response?.data?.detail) || "Invalid invite"));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/register", { email: invite.email, name: form.name, password: form.password, invite_token: token });
      toast.success("Welcome to the team!");
      navigate("/dashboard");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to accept invite");
    } finally { setLoading(false); }
  };

  if (user) return <div className="min-h-screen flex items-center justify-center bg-[var(--peak-bg)]"><div className="section-label">Already signed in — log out first to accept an invite.</div></div>;

  return (
    <div className="min-h-screen bg-[var(--peak-bg)] flex items-center justify-center p-6" data-testid="accept-invite-page">
      <div className="max-w-md w-full bg-white border-2 border-[var(--peak-ink)] p-8">
        {error && <div className="section-label text-[#E50000]">{error}</div>}
        {invite && (
          <>
            <div className="section-label">You're invited by</div>
            <h1 className="font-heading font-black text-2xl tracking-tighter mt-1">{invite.invited_by_email}</h1>
            <p className="text-sm text-[var(--peak-muted)] mt-2">Create your password to join the workspace as <span className="font-mono">{invite.email}</span>.</p>
            <form onSubmit={submit} className="mt-6 space-y-4">
              <div>
                <label className="section-label">Your name</label>
                <input required data-testid="invite-name" className="brutalist-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </div>
              <div>
                <label className="section-label">Password (min 6)</label>
                <input required type="password" minLength={6} data-testid="invite-password" className="brutalist-input" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
              </div>
              <button type="submit" disabled={loading} data-testid="invite-submit" className="w-full bg-[var(--peak-accent)] hover:bg-black text-white font-bold uppercase tracking-widest px-6 py-3 disabled:opacity-60">
                {loading ? "Joining…" : "Accept & join workspace"}
              </button>
            </form>
          </>
        )}
        <div className="mt-4 text-sm"><Link to="/login" className="font-bold underline hover:text-[var(--peak-accent)]">Back to sign in</Link></div>
      </div>
    </div>
  );
}
